import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Star, ChevronDown, ChevronRight } from 'lucide-react';
import { getAccountSummaries, updateAccountGoal } from '../api';
import InlineField from './InlineField';

// On the account page: the account's goal and key-account flag, and its
// finished weekly summaries — the latest in full, earlier weeks folded away.
// Drafts never appear here; a summary arrives when a manager finishes a
// report on the Executive Review page.

const STATUS = {
  on_track: { label: 'On track', className: 'badge-success' },
  at_risk: { label: 'At risk', className: 'badge-warning' },
  stalled: { label: 'Stalled', className: 'badge-gray' },
  needs_decision: { label: 'Needs decision', className: 'badge-danger' },
};

const weekLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function AccountWeeklySummary({ accountId, canEdit }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getAccountSummaries(accountId)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setHidden(true); }); // not for this user — nothing to show
    return () => { cancelled = true; };
  }, [accountId]);

  if (hidden || !data) return null;
  const { account, summaries } = data;
  const [latest, ...earlier] = summaries;
  const internalLatest = summaries.find((s) => s.audience === 'internal') || latest;

  const save = async (fields) => {
    setSaving(true);
    setError('');
    try {
      const { data: saved } = await updateAccountGoal(accountId, fields);
      setData((d) => ({ ...d, account: { ...d.account, goal: saved.goal, is_key_account: saved.is_key_account } }));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  // The goal is edited in place; a rejected save is reported by the field itself.
  const saveGoal = async (goal) => {
    const { data: saved } = await updateAccountGoal(accountId, { goal });
    setData((d) => ({ ...d, account: { ...d.account, goal: saved.goal, is_key_account: saved.is_key_account } }));
  };

  const Bullets = ({ items, risk }) => (
    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.5 }}>
      {items.map((b, i) => <li key={i} style={risk ? { color: '#991b1b' } : undefined}>{b}</li>)}
    </ul>
  );
  const Label = ({ children }) => <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '12px 0 4px' }}>{children}</div>;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={16} /> Goal &amp; weekly summary</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canEdit ? (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => save({ is_key_account: !account.is_key_account })} disabled={saving} aria-pressed={!!account.is_key_account}>
              <Star size={14} style={account.is_key_account ? { fill: '#d97706', color: '#d97706' } : undefined} /> {account.is_key_account ? 'Key account' : 'Mark as key account'}
            </button>
          ) : account.is_key_account ? <span className="badge badge-primary"><Star size={12} /> Key account</span> : null}
        </div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <Label>Goal</Label>
        <p style={{ margin: 0, fontSize: 14.5 }}>
          <InlineField
            value={account.goal || ''}
            onSave={saveGoal}
            canEdit={canEdit}
            label="account goal"
            placeholder="What are we trying to achieve here?"
            emptyText="No goal set. The weekly review reports progress against it."
            maxLength={500}
          />
        </p>

        {!latest ? (
          <p style={{ marginTop: 14, fontSize: 13, color: 'var(--text-secondary)' }}>No weekly summary yet. Managers write one on the <Link to="/executive-review">Executive Review</Link> page.</p>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 14 }}>Week of {weekLabel(internalLatest.week_of)}</strong>
              <span className={`badge ${(STATUS[internalLatest.account_status] || { className: 'badge-gray' }).className}`}>{(STATUS[internalLatest.account_status] || { label: internalLatest.account_status }).label}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{internalLatest.audience === 'customer' ? 'customer-facing' : 'internal'}{internalLatest.created_by_name && ` · by ${internalLatest.created_by_name}`}</span>
            </div>
            <Label>Summary</Label>
            <Bullets items={internalLatest.summary} />
            {internalLatest.what_changed.length > 0 && <><Label>What changed</Label><Bullets items={internalLatest.what_changed} /></>}
            {internalLatest.audience === 'internal' && internalLatest.risks.length > 0 && <><Label>Risks &amp; blockers</Label><Bullets items={internalLatest.risks} risk /></>}

            {earlier.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowHistory(!showHistory)} aria-expanded={showHistory}>
                  {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {earlier.length} earlier week{earlier.length === 1 ? '' : 's'}
                </button>
                {showHistory && (
                  <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
                    {earlier.map((s) => (
                      <li key={s.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border-color, rgba(127,127,127,0.18))' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
                          <strong>Week of {weekLabel(s.week_of)}</strong>
                          <span className={`badge ${(STATUS[s.account_status] || { className: 'badge-gray' }).className}`}>{(STATUS[s.account_status] || { label: s.account_status }).label}</span>
                          <span style={{ color: 'var(--text-secondary)' }}>{s.audience}</span>
                        </div>
                        <Bullets items={s.summary} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
